'use client';
import ProductDto from '@/projectRoot/api-client/interfaces/models/products/Product.dto';
import CubesImage from '../general/cubes-image/CubesImage';
import LinkUrl from '../general/link/LinkUrl';
import style from './product.module.scss';
import { useCallback, useEffect, useState } from 'react';
import { formatPrice } from '@/projectRoot/src/utils/handlers/productsHandler';
import { bodyDisableScroll, colorYiq } from '@/projectRoot/src/utils/handlers/miscHandler';
import Variants from './partials/Variants';
import { useGlobals } from '@/projectRoot/src/contexts/GlobalsContext';
import { useLocale, useTranslations } from 'next-intl';
import { useApiClient } from '@/projectRoot/src/contexts/ApiClientContext';
import Button from '../form-elements/button/Button';
import Heart from '../svg/Heart';
import { useProfile } from '@/projectRoot/src/contexts/ProfileContext';

interface Props {
    product: ProductDto;
    noGroup?: boolean;
    classes?: string[];
    onRemoveFromWishlist?: (productId: string) => void;
    initialIsOnWishlist?: boolean;
}

const Product = ({ product, noGroup = false, classes = [], initialIsOnWishlist = false, onRemoveFromWishlist }: Props) => {
    const t = useTranslations();
    const { apiClient } = useApiClient();
    const { identity, updateWishlist } = useProfile();
    const { isMobileView} = useGlobals();
    const locale = useLocale();
    const [activeProduct, setActiveProduct] = useState<ProductDto>(product);
    const [activeImage, setActiveImage] = useState<string>(activeProduct.images[0]);
    const [showGroupedProducts, setShowGroupedProducts] = useState<boolean>(false);
    const [isOnWishlist, setIsOnWishlist] = useState<boolean>(initialIsOnWishlist ?? false);

    const getProduct = useCallback(async (prod_id: string) => {
        try {
            const response = await apiClient.products().search({ ids: [prod_id] });
            if (response.data) {
                return response.data[0];
            }
        } catch (err) {
            console.log(err);
        }
    }, []);

    const checkIsOnWishlist = async () => {
        try {
            const response = await apiClient.profile().wishlist();
            if (response.data) {
                const onWishlist = response.data.find(value => value.product_id == product.id);
                setIsOnWishlist(onWishlist ? true : false);
            }
        } catch (err) {
            console.log(err);
        }
    };

    const handleWishlistToggle = async (productId: string, isOnWishlist: boolean) => {
        const action = isOnWishlist ? 'remove' : 'add';
        await updateWishlist(productId, action);
        setIsOnWishlist(!isOnWishlist);

        if (action === 'remove' && onRemoveFromWishlist) {
            onRemoveFromWishlist(productId);
        }
    }

    const onSelectProduct = (params: { prod_id: string }) => {
        if (params.prod_id == activeProduct.id) return;
        if (params.prod_id == product.id) {
            setActiveProduct(product);
            setActiveImage(product.images[0]);
        } else {
            // ide api poziv za single zarad info-a od group product dto
            getProduct(params.prod_id).then(prod => {
                setActiveProduct(prod);
                setActiveImage(prod.images[0]);
            });
        }
    }

    const returnLabelColor = (color: string) => {
        return colorYiq(color);
    }

    const onClickShowGroupedProducts = (show: boolean = true) => {
        setShowGroupedProducts(show);
        bodyDisableScroll(isMobileView ? show : isMobileView);
    }

    const onHoverImage = (prod: ProductDto, exit?: boolean) => {
        if (exit) {
            setActiveImage(prod.images[0]);
            return;
        }
        setActiveImage(prod.images[1] ?? prod.images[0]);
    }

    useEffect(() => {
        if (identity && !initialIsOnWishlist) {
            checkIsOnWishlist();
        }
    }, [identity]);

    return (
        <article className={`${style.product} ${classes.join(' ')}`}>
            <div className={style.window}>
                <figure className={style.productGraphics} onMouseEnter={() => onHoverImage(activeProduct)} onMouseLeave={() => onHoverImage(activeProduct, true)}>
                    {
                        activeProduct.labels && activeProduct.labels.length > 0
                            ?
                            <div className={style.labels} onMouseEnter={(e) => e.bubbles}>
                                {
                                    activeProduct.labels.map(label =>
                                        <span
                                            key={label.id}
                                            className={style.label}
                                            style={{ backgroundColor: label.color?.hex ?? 'white', color: label.color?.hex ? returnLabelColor(label.color.hex) : 'black' }}
                                        // onMouseEnter={() => onHoverImage(activeProduct)}
                                        >
                                            {label.title}
                                        </span>
                                    )
                                }
                            </div>
                            : <></>
                    }
                    {
                        identity
                            ?
                            <Button label='wishlist icon' classes={[style.wishlistBtn]} onClick={() => handleWishlistToggle(product.id, isOnWishlist)}>
                                <Heart width={15} height={15} fill={isOnWishlist} />
                            </Button>
                            : <></>
                    }
                    <LinkUrl url={activeProduct.multilang_slugs[locale]} classes={[style.image, 'imgPlaceholder']}>
                        <CubesImage imageNarrow={activeImage} alt={activeProduct.title} />
                    </LinkUrl>
                    <span className={style.plus} onClick={(e) => onClickShowGroupedProducts()} >
                        +
                    </span>
                </figure>
                <div className={`${style.variantsWindow} ${showGroupedProducts ? style.visible : ''}`} onMouseEnter={(e) => e.stopPropagation()} onClick={() => onClickShowGroupedProducts(false)}>
                    <Variants product={activeProduct} classes={[style.variants]} closeWindow={() => onClickShowGroupedProducts(false)} />
                </div>
            </div>
            <div className={style.productInfo}>
                <LinkUrl url={product.multilang_slugs[locale]} text={activeProduct.title} classes={[style.title]} />
                <div className={style.colorPriceWrapper}>
                    {
                        product.grouped_products.length > 0 && !noGroup
                            ?
                            <div className={`${style.colors} ${showGroupedProducts ? style.visible : ''}`}>
                                {/* <span 
                            className={`${style.color} ${activeProduct.color.id == product.color.id ? style.active : ''}`} 
                            style={{background: product.color.image_icon_url ? `url('${product.color.image_icon_url}')` : product.color.color_hex}} 
                            title={product.title + ' ' + product.color.title}
                            onClick={() => onSelectProduct({prod: product, prod_id: product.id})}
                        /> */}
                                {
                                    product.grouped_products.map(item =>
                                        <span
                                            key={item.id}
                                            title={item.title + ' ' + item.color.title}
                                            className={`${style.color} ${activeProduct.color.id == item.color.id ? style.active : ''}`}
                                            style={{ background: item.color.image_icon_url ? `url('${item.color.image_icon_url}')` : item.color.hex }}
                                            onClick={() => onSelectProduct({ prod_id: item.id })}
                                        />
                                    )
                                }
                            </div>
                            : <></>
                    }
                    <p className={style.prices}>
                        <span className={`${style.price} ${activeProduct.base_price !== activeProduct.price ? style.anotherPrice : ''}`}>
                            {formatPrice(activeProduct.base_price)} {process.env.NEXT_PUBLIC_CURRENCY}
                        </span>
                        {
                            activeProduct.base_price !== activeProduct.price
                                ?
                                <span className={style.discountPrice}>
                                    {formatPrice(activeProduct.price)} {process.env.NEXT_PUBLIC_CURRENCY}
                                </span>
                                : <></>
                        }
                    </p>
                </div>
            </div>
        </article>
    )
};

export default Product;
